// src/layouts/Footer.jsx
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, MapPin, Phone, Mail } from "lucide-react";

const socialLinks = [
  { name: "Facebook", icon: "facebook", href: "#" },
  { name: "Instagram", icon: "instagram", href: "#" },
  { name: "LinkedIn", icon: "linkedin", href: "#" },
];

export default function Footer() {
  return (
    <footer className="relative bg-[#fafcff] pt-16 pb-6 border-t-[3px] border-[#006699]/80 overflow-hidden">
      
      {/* Background Subtle Texture */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              -45deg,
              rgba(15, 105, 157, 0.05) 0px,
              rgba(15, 105, 157, 0.05) 1px,
              transparent 1px,
              transparent 18px
            )
          `,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12 pb-12">
          
          {/* Logo & About */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="sm:col-span-2 lg:col-span-1"
          >
            <div className="w-32 mb-6">
              <img
                src="/logosb.png"
                alt="IEEE ISIMS SB Logo"
                className="w-full h-auto"
                style={{
                  filter: "brightness(0) saturate(100%) invert(18%) sepia(87%) saturate(1900%) hue-rotate(185deg) brightness(89%) contrast(102%)",
                }}
              />
            </div>
            <p className="text-[#00334d]/80 text-sm leading-relaxed font-medium pr-4">
              Empowering students to build skills, innovate, and change the world through technology and community.
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="flex flex-col"
          >
            <h5 className="text-[#006699] text-sm font-bold uppercase tracking-widest mb-6 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#006699]"></span> Quick Links
            </h5>
            <ul className="space-y-3">
              {[
                { name: "Home", href: "/" },
                { name: "About Us", href: "/about" },
                { name: "Our Team", href: "/team" },
                { name: "Subunits", href: "/subunits" },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-[#00334d]/80 hover:text-[#006699] font-medium text-sm transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-0 h-[2px] bg-[#006699] transition-all duration-300 group-hover:w-3"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Other Links */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            viewport={{ once: true }}
            className="flex flex-col"
          >
            <h5 className="text-[#006699] text-sm font-bold uppercase tracking-widest mb-6 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#006699]"></span> Discover
            </h5>
            <ul className="space-y-3">
              {[
                { name: "Events", href: "/events" },
                { name: "Awards", href: "/awards" },
                { name: "Contact", href: "/contact" },
                { name: "Privacy Policy", href: "/privacy" },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-[#00334d]/80 hover:text-[#006699] font-medium text-sm transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-0 h-[2px] bg-[#006699] transition-all duration-300 group-hover:w-3"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info & Socials */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="sm:col-span-2 lg:col-span-2 flex flex-col"
          >
            <h5 className="text-[#006699] text-sm font-bold uppercase tracking-widest mb-6 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#006699]"></span> Get In Touch
            </h5>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3 text-[#00334d]/80">
                <MapPin className="w-5 h-5 text-[#006699] mt-0.5 flex-shrink-0" strokeWidth={2} />
                <p className="text-sm font-medium">Sfax, rue Tunis klm 11<br/>Sakiet Ezzit, Sfax 3021</p>
              </div>
              <div className="flex items-center gap-3 text-[#00334d]/80">
                <Phone className="w-5 h-5 text-[#006699] flex-shrink-0" strokeWidth={2} />
                <p className="text-sm font-medium">+216 24 494 492</p>
              </div>
              <div className="flex items-center gap-3 text-[#00334d]/80">
                <Mail className="w-5 h-5 text-[#006699] flex-shrink-0" strokeWidth={2} />
                <p className="text-sm font-medium">sb.isims@ieee.org</p>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 mt-auto">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 border-2 border-[#006699]/30 text-[#006699] flex items-center justify-center hover:bg-[#006699] hover:text-white transition-all duration-300 rounded-none bg-white"
                  title={social.name}
                >
                  {social.icon === "facebook" && <Facebook className="w-4 h-4" strokeWidth={2} />}
                  {social.icon === "instagram" && <Instagram className="w-4 h-4" strokeWidth={2} />}
                  {social.icon === "linkedin" && <Linkedin className="w-4 h-4" strokeWidth={2} />}
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="h-[2px] bg-[#006699]/10 w-full mb-6"></div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left"
        >
          <p className="text-[#00334d]/70 text-xs font-semibold tracking-wide">
            © {new Date().getFullYear()} IEEE ISIMS Student Branch. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="https://mt-portfolio-ashen.vercel.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00334d]/70 text-xs font-semibold hover:text-[#006699] transition-colors duration-300"
            >
              Developed by <span className="font-bold underline decoration-[#006699] underline-offset-4">Mohamed Tounsi</span>
            </a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}